import React from "react";
import { Box, Button, Chip, Divider, Stack, Typography } from "@mui/material";
import { fToNow } from "../../../utils/formatTime";
import {
    getItemKey,
    getItemNotes,
    isCompletedStatus,
} from "../utils/orderItems";
import { parseDateSafe } from "../utils/orderTime";

export default function OrderItemsList({
    items,
    t,
    order,
    showItemStatus = false,
    onAdvanceItem,
    isItemDisabled,
    getNextStatus,
    distanceLocale,
}) {
    const safeItems = Array.isArray(items) ? items : [];
    if (safeItems.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t("kds.noItems")}
            </Typography>
        );
    }
    if (!showItemStatus) {
        return (
            <Stack spacing={1}>
                {safeItems.map((it) => {
                    const notes = getItemNotes(it);
                    return (
                        <Box
                            key={getItemKey(it)}
                            sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "flex-start",
                            }}
                        >
                            <Box sx={{ minWidth: 36 }}>
                                <Chip
                                    size="small"
                                    label={`x${it?.qty ?? 0}`}
                                    sx={{ fontWeight: 700 }}
                                    color="default"
                                />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 700, lineHeight: 1.2 }}
                                >
                                    {it?.item_name ||
                                        it?.item_code ||
                                        t("kds.itemFallback")}
                                </Typography>
                                {notes ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ whiteSpace: "pre-wrap" }}
                                    >
                                        {notes}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
        );
    }

    const orderedItems = safeItems
        .map((item, index) => ({
            item,
            index,
            completed: isCompletedStatus(item?.status),
        }))
        .sort((a, b) => {
            if (a.completed !== b.completed) {
                return a.completed ? 1 : -1;
            }
            return a.index - b.index;
        })
        .map((entry) => entry.item);
    const knownIdentifiers = new Set(
        safeItems.map((it) => it?.identifier).filter(Boolean)
    );

    const renderItemRow = (it, isAddon = false) => {
        const notes = getItemNotes(it);
        const nextStatus = getNextStatus?.(it, safeItems, order);
        const canAdvance = !!order && !!it?.identifier && !!nextStatus;
        const actionSlotWidth = 104;
        const completedAt =
            isCompletedStatus(it?.status) && it?.status_change_date
                ? parseDateSafe(it.status_change_date)
                : null;

        return (
            <Box
                key={getItemKey(it, isAddon ? "-addon" : "")}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    pl: isAddon ? 1 : 0,
                    py: 0.35,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        gap: 0.75,
                    }}
                >
                    <Box
                        sx={{
                            minWidth: 36,
                            display: "flex",
                            justifyContent: "flex-start",
                        }}
                    >
                        <Chip
                            size="small"
                            label={`x${it?.qty ?? 0}`}
                            sx={{ fontWeight: 700, mt: 0.1 }}
                            color="default"
                        />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 800,
                                lineHeight: 1.25,
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                            }}
                        >
                            {it?.item_name ||
                                it?.item_code ||
                                t("kds.itemFallback")}
                        </Typography>
                        {notes ? (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    whiteSpace: "normal",
                                    overflowWrap: "anywhere",
                                    display: "block",
                                    mt: 0.25,
                                }}
                            >
                                {notes}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 0.8,
                        pl: 4.6,
                        minHeight: 30,
                    }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        {it?.status ? (
                            <Stack
                                direction="row"
                                spacing={0.6}
                                alignItems="center"
                                flexWrap="nowrap"
                                sx={{ minWidth: 0 }}
                            >
                                <Chip
                                    size="small"
                                    label={it.status}
                                    color={canAdvance ? "warning" : "success"}
                                    sx={{
                                        height: 22,
                                        maxWidth: "100%",
                                        "& .MuiChip-label": {
                                            whiteSpace: "nowrap",
                                        },
                                    }}
                                />
                                {completedAt ? (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ whiteSpace: "nowrap", lineHeight: 1 }}
                                    >
                                        {fToNow(completedAt, distanceLocale)}
                                    </Typography>
                                ) : null}
                            </Stack>
                        ) : null}
                    </Box>

                    <Box
                        sx={{
                            width: actionSlotWidth,
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    >
                        {canAdvance ? (
                            <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => onAdvanceItem?.(order, it, safeItems)}
                                disabled={isItemDisabled?.(
                                    order?.name,
                                    it?.identifier
                                )}
                                sx={{
                                    width: "100%",
                                    minHeight: 30,
                                    px: 0.75,
                                    whiteSpace: "nowrap",
                                    fontWeight: 700,
                                    lineHeight: 1,
                                }}
                            >
                                {t("kds.markReady")}
                            </Button>
                        ) : (
                            <Box sx={{ width: "100%", minHeight: 30 }} />
                        )}
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Stack spacing={0.35}>
            {orderedItems.map((it, index) => {
                const parentId = it?.parent_item;
                const isAddon =
                    !!parentId &&
                    parentId !== "NULL" &&
                    knownIdentifiers.has(parentId);
                return (
                    <React.Fragment key={getItemKey(it, "-flat")}>
                        <Box
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                px: 0.75,
                                py: 0.5,
                            }}
                        >
                            {renderItemRow(it, isAddon)}
                        </Box>
                        {index < orderedItems.length - 1 ? (
                            <Divider
                                sx={{ borderColor: "divider", opacity: 0.6 }}
                            />
                        ) : null}
                    </React.Fragment>
                );
            })}
        </Stack>
    );
}
